'use client';

import type { useReportWebVitals } from 'next/web-vitals';
import getDeviceType from '@/libraries/getters/get-device-type';
import { trackClientMetric } from './telemetry-client';

type WebVitalsMetric = Parameters<Parameters<typeof useReportWebVitals>[0]>[0];

const UNKNOWN_PAGE_TEMPLATE = 'Unknown';

let currentPageTemplate: string | undefined;
let landingPageTemplate: string | undefined;

if (typeof window !== 'undefined') {
  // A back/forward cache restore keeps this module's state, but web vitals start
  // a new measurement cycle for the restored page, so the template is captured
  // again. Registered on import, before `useReportWebVitals` adds its own
  // capturing listener, because TTFB is reported synchronously inside it.
  window.addEventListener(
    'pageshow',
    event => {
      if (event.persisted) {
        landingPageTemplate = currentPageTemplate ?? UNKNOWN_PAGE_TEMPLATE;
      }
    },
    { capture: true },
  );
}

/**
 * Records the template of the page currently on screen. Called by the page
 * layouts so the web vitals can be segmented per template.
 */
export const setPageTemplate = (pageTemplate?: string) => {
  currentPageTemplate = pageTemplate;
};

/**
 * Sends a web vitals metric to Application Insights as a custom metric,
 * tagged with the page template and device type.
 *
 * Must stay a stable module-level function: `useReportWebVitals` registers
 * new listeners every time its callback changes, which would double report.
 */
export const reportWebVitals = (metric: WebVitalsMetric) => {
  // Web vitals only measure the hard-loaded document, so the template is locked
  // on the first report. Otherwise CLS and INP, which are reported when the page
  // is hidden, would be tagged with whatever page the user navigated to last.
  landingPageTemplate ??= currentPageTemplate ?? UNKNOWN_PAGE_TEMPLATE;

  trackClientMetric(`WebVitals.${metric.name}`, metric.value, {
    deviceType: getDeviceType(),
    metricId: metric.id,
    navigationType: metric.navigationType,
    pageTemplate: landingPageTemplate,
    rating: metric.rating,
  });
};
